/**
 * Tool Combo Template Editor
 *
 * Reusable editor for a single tool combo template. Used by:
 * - System Settings → ToolComboPanel (right split pane, full CRUD)
 * - Agent Config → ToolComboSection (drawer for inline template editing)
 *
 * Owns local UI state (tool picker open/closed, search query). Emits the
 * updated template via onChange — parent decides how to persist.
 */

import React, { useMemo, useState, useEffect } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Search,
  Check,
  Plus,
  Trash2,
  Eye,
  X,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import type { ToolComboTemplate, ToolComboTool } from "@/types/system";

export interface ToolComboAvailableTool {
  name: string;
  description: string;
  category: string;
  type?: "builtin" | "custom";
  displayNameCn?: string;
  displayNameEn?: string;
  descriptionCn?: string;
  descriptionEn?: string;
}

export type ToolComboEditorMessages = Partial<{
  templateId: string;
  nameCn: string;
  nameEn: string;
  descriptionCn: string;
  descriptionEn: string;
  guidance: string;
  guidancePlaceholder: string;
  enabled: string;
  tools: string;
  addTool: string;
  selectTool: string;
  usageInstruction: string;
  usageInstructionPlaceholder: string;
  preview: string;
  closePicker: string;
  noTools: string;
  searchTools: string;
  clickToAdd: string;
  deleteTemplate: string;
}>;

interface ToolComboTemplateEditorProps {
  template: ToolComboTemplate;
  availableTools: ToolComboAvailableTool[];
  isChinese: boolean;
  t?: ToolComboEditorMessages;
  onChange: (next: ToolComboTemplate) => void;
  onPreview?: () => void;
  previewPending?: boolean;
  /** When set, preview button is disabled with this string as tooltip. */
  previewDisabledReason?: string;
  /** When provided, renders a Delete button alongside Preview. */
  onDelete?: () => void;
}

export const ToolComboTemplateEditor: React.FC<
  ToolComboTemplateEditorProps
> = ({
  template,
  availableTools,
  isChinese,
  t,
  onChange,
  onPreview,
  previewPending,
  previewDisabledReason,
  onDelete,
}) => {
  const [showToolPicker, setShowToolPicker] = useState(false);
  const [toolSearchQuery, setToolSearchQuery] = useState("");

  // Reset picker state when switching templates
  useEffect(() => {
    setShowToolPicker(false);
    setToolSearchQuery("");
  }, [template.id]);

  const filteredAvailableTools = useMemo(() => {
    if (!toolSearchQuery) return availableTools;
    const query = toolSearchQuery.toLowerCase();
    return availableTools.filter(
      (tool) =>
        tool.name.toLowerCase().includes(query) ||
        tool.description.toLowerCase().includes(query) ||
        tool.category.toLowerCase().includes(query) ||
        tool.displayNameCn?.toLowerCase().includes(query) ||
        tool.displayNameEn?.toLowerCase().includes(query) ||
        tool.descriptionCn?.toLowerCase().includes(query) ||
        tool.descriptionEn?.toLowerCase().includes(query),
    );
  }, [availableTools, toolSearchQuery]);

  const addedToolNames = useMemo(
    () => new Set(template.tools.map((t) => t.tool_name)),
    [template.tools],
  );

  const updateField = <K extends keyof ToolComboTemplate>(
    field: K,
    value: ToolComboTemplate[K],
  ) => {
    onChange({ ...template, [field]: value });
  };

  const updateTool = (
    index: number,
    field: keyof ToolComboTool,
    value: unknown,
  ) => {
    const nextTools = template.tools.map((tool, i) =>
      i === index ? { ...tool, [field]: value } : tool,
    );
    onChange({ ...template, tools: nextTools });
  };

  const addTool = (toolInfo?: { name: string; description: string }) => {
    if (toolInfo && template.tools.some((t) => t.tool_name === toolInfo.name))
      return;
    const maxOrder = Math.max(0, ...template.tools.map((t) => t.order));
    const newTool: ToolComboTool = {
      tool_name: toolInfo?.name || "",
      order: maxOrder + 1,
      usage_instruction: toolInfo?.description || "",
    };
    onChange({ ...template, tools: [...template.tools, newTool] });
  };

  const removeTool = (index: number) => {
    const nextTools = template.tools
      .filter((_, i) => i !== index)
      .map((tool, i) => ({ ...tool, order: i + 1 }));
    onChange({ ...template, tools: nextTools });
  };

  const moveTool = (index: number, direction: "up" | "down") => {
    const target = direction === "up" ? index - 1 : index + 1;
    if (target < 0 || target >= template.tools.length) return;
    const nextTools = [...template.tools];
    [nextTools[index], nextTools[target]] = [
      nextTools[target],
      nextTools[index],
    ];
    nextTools.forEach((tool, i) => {
      tool.order = i + 1;
    });
    onChange({ ...template, tools: nextTools });
  };

  return (
    <div className="p-4 space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t?.templateId || "Template ID"}
        </label>
        <Input value={template.id} disabled className="font-mono bg-gray-50" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t?.nameCn || "Name (Chinese)"}
          </label>
          <Input
            value={template.name_cn || ""}
            onChange={(e) => updateField("name_cn", e.target.value)}
            placeholder="例如：基础占星组合"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t?.nameEn || "Name (English)"}
          </label>
          <Input
            value={template.name_en || ""}
            onChange={(e) => updateField("name_en", e.target.value)}
            placeholder="e.g., Basic Astrology Combo"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t?.descriptionCn || "Description (Chinese)"}
          </label>
          <textarea
            value={template.description_cn || ""}
            onChange={(e) => updateField("description_cn", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
            rows={2}
            placeholder="模板描述..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t?.descriptionEn || "Description (English)"}
          </label>
          <textarea
            value={template.description_en || ""}
            onChange={(e) => updateField("description_en", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
            rows={2}
            placeholder="Template description..."
          />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">
          {t?.enabled || "Enabled"}
        </label>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={template.enabled !== false}
            onChange={(e) => updateField("enabled", e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>

      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-gray-900">
            {t?.tools || "Tools"}
          </h3>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowToolPicker(!showToolPicker)}
          >
            {showToolPicker ? (
              <X className="w-4 h-4 mr-1" />
            ) : (
              <Plus className="w-4 h-4 mr-1" />
            )}
            {showToolPicker
              ? t?.closePicker || "Close"
              : t?.addTool || "Add Tool"}
          </Button>
        </div>

        {showToolPicker && (
          <div className="mb-4 border rounded-lg bg-blue-50/50 overflow-hidden">
            <div className="p-2 border-b bg-blue-50">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder={t?.searchTools || "Search available tools..."}
                  value={toolSearchQuery}
                  onChange={(e) => setToolSearchQuery(e.target.value)}
                  className="pl-8 h-8 text-sm bg-white"
                />
              </div>
            </div>
            <div className="max-h-48 overflow-y-auto p-1">
              {filteredAvailableTools.length === 0 ? (
                <div className="text-center text-gray-400 py-4 text-sm">
                  {isChinese ? "没有找到匹配的工具" : "No matching tools found"}
                </div>
              ) : (
                <div className="space-y-0.5">
                  {filteredAvailableTools.map((tool) => {
                    const isAdded = addedToolNames.has(tool.name);
                    const displayName = isChinese
                      ? tool.displayNameCn || tool.displayNameEn || tool.name
                      : tool.displayNameEn || tool.displayNameCn || tool.name;
                    const displayDesc = isChinese
                      ? tool.descriptionCn ||
                        tool.descriptionEn ||
                        tool.description
                      : tool.descriptionEn ||
                        tool.descriptionCn ||
                        tool.description;
                    return (
                      <button
                        key={tool.name}
                        onClick={() => {
                          if (!isAdded)
                            addTool({
                              name: tool.name,
                              description: displayDesc,
                            });
                        }}
                        disabled={isAdded}
                        className={`w-full text-left px-2 py-1.5 rounded text-sm transition-colors ${
                          isAdded
                            ? "bg-green-100 text-green-700 cursor-not-allowed"
                            : "hover:bg-blue-100 text-gray-700"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <span className="font-medium truncate">
                              {displayName}
                            </span>
                            {displayName !== tool.name && (
                              <span className="text-xs text-gray-400 font-mono">
                                {tool.name}
                              </span>
                            )}
                            <span className="text-xs text-gray-400 px-1 py-0.5 bg-gray-100 rounded">
                              {tool.category}
                            </span>
                          </div>
                          {isAdded ? (
                            <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                          ) : (
                            <Plus className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {displayDesc}
                        </p>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="px-2 py-1.5 border-t bg-blue-50 text-xs text-gray-500">
              {filteredAvailableTools.length}{" "}
              {isChinese ? "个工具可选" : "tools available"} ·{" "}
              {addedToolNames.size} {isChinese ? "个已添加" : "added"}
            </div>
          </div>
        )}

        {template.tools.length === 0 ? (
          <div className="text-center text-gray-400 py-4 border border-dashed rounded-lg">
            {t?.noTools || "No tools added"}
            {!showToolPicker && (
              <button
                onClick={() => setShowToolPicker(true)}
                className="block mx-auto mt-2 text-blue-500 text-sm hover:underline"
              >
                {t?.clickToAdd || "Click to add tools"}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {template.tools.map((tool, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-3 border">
                <div className="flex items-start gap-2">
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={() => moveTool(index, "up")}
                      disabled={index === 0}
                      className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <span className="text-xs text-gray-500 text-center">
                      {tool.order}
                    </span>
                    <button
                      onClick={() => moveTool(index, "down")}
                      disabled={index === template.tools.length - 1}
                      className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-gray-900">
                        {tool.tool_name || t?.selectTool || "Select tool"}
                      </span>
                      {!tool.tool_name && (
                        <select
                          value={tool.tool_name}
                          onChange={(e) => {
                            const selectedTool = availableTools.find(
                              (at) => at.name === e.target.value,
                            );
                            updateTool(index, "tool_name", e.target.value);
                            if (selectedTool && !tool.usage_instruction) {
                              updateTool(
                                index,
                                "usage_instruction",
                                selectedTool.description,
                              );
                            }
                          }}
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md"
                        >
                          <option value="">
                            {t?.selectTool || "Select tool"}
                          </option>
                          {availableTools.map((at) => (
                            <option key={at.name} value={at.name}>
                              {at.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        {t?.usageInstruction || "Usage Instruction"}
                      </label>
                      <textarea
                        value={tool.usage_instruction || ""}
                        onChange={(e) =>
                          updateTool(index, "usage_instruction", e.target.value)
                        }
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md resize-y min-h-[60px]"
                        rows={3}
                        placeholder={
                          t?.usageInstructionPlaceholder ||
                          "Describe when and how to use this tool..."
                        }
                      />
                    </div>
                  </div>

                  <button
                    onClick={() => removeTool(index)}
                    className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border-t pt-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t?.guidance || "Guidance"}
        </label>
        <textarea
          value={template.guidance || ""}
          onChange={(e) => updateField("guidance", e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-y min-h-[80px]"
          rows={4}
          placeholder={
            t?.guidancePlaceholder ||
            "Additional guidance for using this tool combo..."
          }
        />
      </div>

      {(onPreview || onDelete) && (
        <div className="border-t pt-4 flex items-center justify-between">
          {onPreview ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={onPreview}
              disabled={previewPending || !!previewDisabledReason}
              title={previewDisabledReason || ""}
            >
              <Eye className="w-4 h-4 mr-1" />
              {previewPending ? "..." : t?.preview || "Preview"}
            </Button>
          ) : (
            <span />
          )}
          {onDelete && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onDelete}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              {t?.deleteTemplate || "Delete"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
